import type React from 'react'
import { useEffect } from 'react'

// Move the core functionality to a separate utility function
// to keep the component lightweight
const setupImagePreloadEffect = () => {
  // Function to check if the image src has a valid image extension
  const isValidImageSrc = (src: string | null): boolean => {
    if (!src) return false
    const validExtensions = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i
    return validExtensions.test(src)
  }

  // Set to keep track of processed images to avoid duplicates
  const processedImages = new WeakSet<HTMLImageElement>()

  const loadImage = (img: HTMLImageElement) => {
    // Skip if already processed or invalid src
    if (processedImages.has(img) || !isValidImageSrc(img.getAttribute('src'))) {
      img.style.opacity = '1'
      return
    }

    // Mark as processed
    processedImages.add(img)

    const placeholder = document.createElement('div')
    placeholder.className =
      'absolute inset-0 transition-all bg-gray-200 animate-pulse rounded-inherit'
    img.parentNode?.insertBefore(placeholder, img)

    img.style.opacity = '0'
    img.style.transition = 'opacity 300ms ease-in-out'

    // Set a timeout to handle cases where the image might never load
    const timeoutId = setTimeout(() => placeholder.remove(), 5000) // Reduced timeout

    const handleLoad = () => {
      img.style.opacity = '1'
      placeholder.style.opacity = '0'
      setTimeout(() => placeholder.remove(), 500)
      clearTimeout(timeoutId)

      // Clean up event listeners
      img.removeEventListener('load', handleLoad)
      img.removeEventListener('error', handleError)
    }

    const handleError = () => {
      placeholder.remove()
      img.style.opacity = '1'
      clearTimeout(timeoutId)

      // Clean up event listeners
      img.removeEventListener('load', handleLoad)
      img.removeEventListener('error', handleError)
    }

    if (img.complete) {
      handleLoad()
    } else {
      img.addEventListener('load', handleLoad)
      img.addEventListener('error', handleError)
    }
  }

  // Function to process new images with debounce
  let debounceTimer: number | null = null
  const processBatch = (images: HTMLImageElement[]) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = window.setTimeout(() => {
      images.forEach((img) => {
        if (!processedImages.has(img)) {
          observer.observe(img)
        }
      })
    }, 100)
  }

  // Intersection Observer for lazy loading
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadImage(entry.target as HTMLImageElement)
          observer.unobserve(entry.target)
        }
      })
    },
    {
      rootMargin: '100px',
      threshold: 0.1,
    }
  )

  // Initial processing of images
  const processExistingImages = () => {
    const images = Array.from(document.querySelectorAll('img'))
    processBatch(images)
  }

  // Set up mutation observer to catch dynamically added images
  const mutationCallback = (mutationsList: MutationRecord[]) => {
    const newImages: HTMLImageElement[] = []

    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          // Check if node itself is an image
          if (node.nodeName === 'IMG') {
            newImages.push(node as HTMLImageElement)
          }

          // Check for images within added nodes
          if (node.nodeType === Node.ELEMENT_NODE) {
            const nestedImages = (node as Element).querySelectorAll('img')
            nestedImages.forEach((img) => newImages.push(img))
          }
        })
      }
    }

    if (newImages.length > 0) {
      processBatch(newImages)
    }
  }

  const mutationObserver = new MutationObserver(mutationCallback)
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Initial processing of images
  processExistingImages()

  // Cleanup function
  return () => {
    observer.disconnect()
    mutationObserver.disconnect()
    if (debounceTimer) clearTimeout(debounceTimer)
  }
}

const GlobalImagePreloadEffect: React.FC = () => {
  useEffect(() => {
    return setupImagePreloadEffect()
  }, [])

  return null
}

export default GlobalImagePreloadEffect
